import { create } from 'zustand'
import {
  appsFlyerApi,
  checkingAccountApi,
  segmentApi,
  userApi,
  bankingApi,
} from '@root/api'
import ICheckingAccount from '@root/types/CheckingAccount'
import { useOnboardingStore } from './onboardingStore'
import { useUserStore } from './userStore'

interface IRoarMoney {
  accountNumber: string
  availableBalance: number
}

interface RoarMoneyInitialState {
  roarMoney?: IRoarMoney | null
}
export interface RoarMoneyState extends RoarMoneyInitialState {
  getRoarMoneyDetails: () => Promise<ICheckingAccount | undefined>
  createRMCheckingAccount: (
    jobCode: number,
    incomeCode: number,
    paymentTypeCode: string,
    signal?: AbortSignal
  ) => Promise<string | undefined>
  reset: () => void
}

const initialState: RoarMoneyInitialState = {
  roarMoney: undefined,
}
const useRoarMoneyStore = create<RoarMoneyState>()((set) => ({
  ...initialState,
  /**
   * @error: Sample
   * {
   *    code: "404",
   *    data: {
   *      requestErrors: [
   *        "No virtual account found for ml user ID 64b8f8520891db718fbeab5e"
   *      ]
   *    }
   * }
   */
  getRoarMoneyDetails: async () => {
    try {
      const resp = await checkingAccountApi.fetchCheckingAccount()

      if (resp.data) {
        set({
          roarMoney: {
            availableBalance: resp?.data.availableBalance || 0,
            accountNumber: resp?.data.achInformation?.accountNumber || '',
          },
        })
      }

      return resp.data
    } catch (error: any) {
      // If user do not have account
      if (error?.code === '404') {
        set({ roarMoney: null })
      }

      return Promise.reject(error)
    }
  },
  createRMCheckingAccount: async (
    jobCode: number,
    incomeCode: number,
    paymentTypeCode: string
  ) => {
    const isIntentFlow = useOnboardingStore.getState().isIntentFlow

    const response = await userApi.getUserProfile()

    if (
      !response ||
      !response.data ||
      !response.data.address ||
      !response.data.address.id
    ) {
      throw new Error('MISSING_VALUES')
    }

    const { data } = await bankingApi.createCheckingAccount({
      depositCode: incomeCode,
      employmentCode: jobCode,
      addressId: response.data.address.id,
      leadGenTransactionId: useUserStore.getState().leadGenTransactionId,
      paymentForm: paymentTypeCode,
    })

    const message = data && data.message
    const referrer = isIntentFlow ? 'intent' : 'organic'

    if (message === 'Created') {
      segmentApi.trackWithOSPlatform('rm_account_create_submit', {
        status: 'success',
        referrer,
      })
      segmentApi.trackWithOSPlatform('DDA Account Created')
      appsFlyerApi.event('RM Account Created')
    } else {
      segmentApi.trackWithOSPlatform('rm_account_create_submit', {
        status: 'fail',
        fail_reason: message,
        referrer,
      })
    }

    return message
  },
  reset: () => {
    set(initialState)
  },
}))

export { useRoarMoneyStore }
